/**
 * Typed same-origin fetch client for the LifeAdvice admin API
 * (backend/lifescribe-advice/src/routes/admin.js, mounted at
 * /api/advice/admin), reached through the `/api/admin/lifeadvice` proxy:
 *
 *   production → backend/routes/admin/lifeAdviceProxy.js on admin-backend
 *   local dev  → the webpack dev-server proxy entry of the same path
 *
 * Auth is entirely the proxy's job — the browser carries the normal LifeScribe
 * admin session cookie (HttpOnly), the proxy verifies it and forwards the
 * caller's own credential upstream, where the advice service re-verifies
 * users.admin_role. This client therefore sets NO Authorization header, exactly
 * like quillAdminService.ts (whose request/unwrap/error-class/qs structure this
 * file follows).
 *
 * Response envelope (uniform across every route in admin.js):
 *   success → { success: true, data: T, service: 'lifescribe-advice' }
 *             list routes add { pagination: { total, limit, offset, page } }
 *             analytics routes add { windowDays }
 *   failure → { success: false, error: '<CODE>', message: '<human>', service }
 *
 * Note the failure envelope uses `error` for the machine code (the Quill
 * backend uses `code`) — LifeAdviceApiError.code reads `error` first and falls
 * back to `code` so both shapes work.
 *
 * GET /entries/export is the one non-JSON route: it replies text/csv, so it has
 * its own `requestText` path and returns a raw string.
 */

const BASE = '/api/admin/lifeadvice';

// ── Error type ────────────────────────────────────────────────────────────

/**
 * Thrown for any non-2xx response or any `{ success: false }` body. `code`
 * carries the service's machine-readable error (INVALID_SLUG, SLUG_EXISTS,
 * INSUFFICIENT_PRIVILEGES, CATEGORY_NOT_FOUND, CSV_MISSING_COLUMNS, …) so
 * callers can branch without string-matching the message.
 */
export class LifeAdviceApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'LifeAdviceApiError';
    this.status = status;
    this.code = code;
  }
}

/** True when the failure is "you're a view_only admin" rather than a real fault. */
export function isPrivilegeError(e: unknown): boolean {
  return e instanceof LifeAdviceApiError && e.status === 403;
}

/** True when the proxy or the advice service itself is unreachable/not configured. */
export function isBackendDownError(e: unknown): boolean {
  return (
    e instanceof LifeAdviceApiError &&
    (e.status === 0 || e.status === 502 || e.status === 503 || e.status === 504)
  );
}

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  data?: unknown;
  pagination?: Pagination;
  windowDays?: number;
  [key: string]: unknown;
}

/** Builds a `?a=1&b=2` query string, skipping undefined/null/empty values. */
function qs(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Fetch + JSON. Throws LifeAdviceApiError on !ok or `{ success: false }`.
 * A network-level failure (dev server down, proxy unroutable) surfaces as
 * status 0 / code NETWORK_ERROR so pages can show the "backend unreachable"
 * empty state rather than a generic error.
 */
async function request<T = EnvelopeShape>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    throw new LifeAdviceApiError(
      e instanceof Error ? e.message : 'Network error contacting the LifeAdvice service',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message =
      body?.message ||
      body?.error ||
      `LifeAdvice request failed (${res.status})`;
    throw new LifeAdviceApiError(message, res.status, body?.error || body?.code);
  }

  return (body ?? {}) as T;
}

/** Same as `request`, but for the text/csv export route. */
async function requestText(path: string): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  } catch (e) {
    throw new LifeAdviceApiError(
      e instanceof Error ? e.message : 'Network error contacting the LifeAdvice service',
      0,
      'NETWORK_ERROR'
    );
  }

  const text = await res.text();

  if (!res.ok) {
    let message = `LifeAdvice export failed (${res.status})`;
    let code: string | undefined;
    try {
      const parsed = JSON.parse(text) as EnvelopeShape;
      message = parsed.message || parsed.error || message;
      code = parsed.error || parsed.code;
    } catch {
      // Non-JSON error body — keep the generic message.
    }
    throw new LifeAdviceApiError(message, res.status, code);
  }

  return text;
}

/** Unwrap the `{ success, data }` envelope → data (falls back to the raw body). */
function unwrap<T>(body: unknown): T {
  const b = body as { data?: T } | null | undefined;
  if (b && typeof b === 'object' && 'data' in b && b.data !== undefined) {
    return b.data as T;
  }
  return body as T;
}

// ════════════════════════════════════════════════════════════════════════
// Models — mirror the SELECTs in lifescribe-advice/src/routes/admin.js
// ════════════════════════════════════════════════════════════════════════

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  page: number;
}

/** A list response with its server-side pagination block. */
export interface Paged<T> {
  rows: T[];
  pagination: Pagination;
}

const EMPTY_PAGINATION: Pagination = { total: 0, limit: 0, offset: 0, page: 1 };

function paged<T>(body: EnvelopeShape): Paged<T> {
  return {
    rows: (unwrap<T[]>(body) || []) as T[],
    pagination: body.pagination || EMPTY_PAGINATION,
  };
}

/** wisdom_categories row + the two counts the admin list query adds. */
export interface WisdomCategory {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
  light_bg: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  /** Denormalized column maintained by the service (active prompts). */
  prompt_count?: number;
  /** Computed by GET /categories only. */
  total_prompts?: number;
  /** Computed by GET /categories only. */
  total_curated?: number;
  created_at: string;
  updated_at: string;
}

export interface CategoryInput {
  slug?: string;
  name?: string;
  icon?: string;
  color?: string;
  light_bg?: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export interface WisdomPrompt {
  id: string;
  category_id: string;
  category_slug?: string;
  category_name?: string;
  title: string;
  prompt_text: string;
  description: string | null;
  is_popular: boolean;
  is_active: boolean;
  sort_order: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface PromptInput {
  category_id?: string;
  title?: string;
  prompt_text?: string;
  description?: string | null;
  is_popular?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

/** Result of POST /prompts/bulk. */
export interface BulkImportResult {
  inserted: number;
  skipped: number;
  errors: Array<{ line: number; error: string }>;
}

export interface CuratedWisdom {
  id: string;
  category_id: string;
  category_slug?: string;
  category_name?: string;
  title: string;
  body: string;
  author: string;
  tone: string | null;
  is_active: boolean;
  sort_order: number;
  usage_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CuratedInput {
  category_id?: string;
  title?: string;
  body?: string;
  author?: string;
  tone?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export type EntryStatus = 'draft' | 'recorded' | 'transformed' | 'shared';

/**
 * Row shape of GET /entries. NOTE: the admin list deliberately omits the
 * advice text — only GET /entries/:id returns original/transformed text.
 */
export interface WisdomEntryRow {
  id: string;
  user_id: string;
  category_id: string | null;
  category_slug: string | null;
  category_name: string | null;
  prompt_id: string | null;
  curated_id: string | null;
  title: string | null;
  status: EntryStatus | string;
  word_count: number;
  recipient_name: string | null;
  recipient_relation: string | null;
  is_personalized: boolean;
  has_ai_version: boolean;
  has_voice_clone: boolean;
  shared_via: string | null;
  shared_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WisdomShare {
  id: string;
  share_method: string;
  recipient_identifier: string | null;
  opened_at: string | null;
  created_at: string;
}

/** GET /entries/:id — the full row (e.*) plus its share history. */
export interface WisdomEntryDetail extends WisdomEntryRow {
  original_text: string | null;
  transformed_text: string | null;
  transcription_text: string | null;
  audio_url: string | null;
  audio_duration_seconds: number | null;
  voice_clone_url: string | null;
  writing_style: string | null;
  ai_version: number | null;
  prompt_title: string | null;
  shares: WisdomShare[];
}

export interface AnalyticsSummary {
  windowDays: number;
  total_entries: number;
  total_creators: number;
  shared_entries: number;
  draft_entries: number;
  ai_transformed_entries: number;
  voice_cloned_entries: number;
  total_shares: number;
  avg_word_count: number;
  entries_in_window: number;
  creators_in_window: number;
  active_categories: number;
  active_prompts: number;
  active_curated: number;
}

export interface EntriesOverTimePoint {
  day: string;
  entries_created: number;
  entries_shared: number;
}

export interface CategoryDistributionRow {
  category_id: string;
  slug: string;
  name: string;
  color: string;
  entry_count: number;
  shared_count: number;
}

export interface ShareMethodRow {
  share_method: string;
  share_count: number;
  user_count: number;
  entry_count: number;
}

export interface TopPromptRow {
  prompt_id: string;
  title: string;
  usage_count: number;
  category_slug: string;
  category_name: string;
  color: string;
  entry_count: number;
  shared_count: number;
}

export interface FunnelStage {
  stage: 'started' | 'captured' | 'transformed' | 'voice_cloned' | 'shared' | string;
  label: string;
  count: number;
  percent: number;
}

export interface FunnelResult {
  windowDays: number;
  stages: FunnelStage[];
}

// ════════════════════════════════════════════════════════════════════════
// API surface
// ════════════════════════════════════════════════════════════════════════

export interface ListQuery {
  category_id?: string;
  is_active?: boolean;
  status?: string;
  user_id?: string;
  limit?: number;
  page?: number;
  offset?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export const lifeAdviceApi = {
  categories: {
    /** GET /categories — always the full list, sorted by sort_order. */
    list: async (): Promise<WisdomCategory[]> =>
      unwrap<WisdomCategory[]>(await request(`/categories`)) || [],

    create: async (input: CategoryInput): Promise<WisdomCategory> =>
      unwrap<WisdomCategory>(
        await request(`/categories`, { method: 'POST', ...json(input) })
      ),

    update: async (id: string, input: CategoryInput): Promise<WisdomCategory> =>
      unwrap<WisdomCategory>(
        await request(`/categories/${encodeURIComponent(id)}`, { method: 'PUT', ...json(input) })
      ),

    /** Soft delete — sets is_active=false (member entries reference categories). */
    deactivate: async (id: string): Promise<WisdomCategory> =>
      unwrap<WisdomCategory>(
        await request(`/categories/${encodeURIComponent(id)}`, { method: 'DELETE' })
      ),

    /** PUT /categories/reorder — returns the whole list in its new order. */
    reorder: async (order: Array<{ id: string; sort_order: number }>): Promise<WisdomCategory[]> =>
      unwrap<WisdomCategory[]>(
        await request(`/categories/reorder`, { method: 'PUT', ...json({ order }) })
      ) || [],
  },

  prompts: {
    list: async (query?: ListQuery): Promise<Paged<WisdomPrompt>> =>
      paged<WisdomPrompt>(await request(`/prompts${qs(query as never)}`)),

    create: async (input: PromptInput): Promise<WisdomPrompt> =>
      unwrap<WisdomPrompt>(await request(`/prompts`, { method: 'POST', ...json(input) })),

    update: async (id: string, input: PromptInput): Promise<WisdomPrompt> =>
      unwrap<WisdomPrompt>(
        await request(`/prompts/${encodeURIComponent(id)}`, { method: 'PUT', ...json(input) })
      ),

    /**
     * DELETE /prompts/:id — hard delete when unused, otherwise the service
     * deactivates instead and flags `deactivated: true` in the payload.
     */
    remove: async (
      id: string
    ): Promise<{ id?: string; deleted?: boolean; deactivated?: boolean; reason?: string }> =>
      unwrap(await request(`/prompts/${encodeURIComponent(id)}`, { method: 'DELETE' })),

    /** POST /prompts/bulk — raw CSV text with a category_slug/title/prompt_text header. */
    bulkImport: async (csv: string): Promise<BulkImportResult> =>
      unwrap<BulkImportResult>(await request(`/prompts/bulk`, { method: 'POST', ...json({ csv }) })),
  },

  curated: {
    list: async (query?: ListQuery): Promise<Paged<CuratedWisdom>> =>
      paged<CuratedWisdom>(await request(`/curated${qs(query as never)}`)),

    create: async (input: CuratedInput): Promise<CuratedWisdom> =>
      unwrap<CuratedWisdom>(await request(`/curated`, { method: 'POST', ...json(input) })),

    update: async (id: string, input: CuratedInput): Promise<CuratedWisdom> =>
      unwrap<CuratedWisdom>(
        await request(`/curated/${encodeURIComponent(id)}`, { method: 'PUT', ...json(input) })
      ),

    remove: async (
      id: string
    ): Promise<{ id?: string; deleted?: boolean; deactivated?: boolean; reason?: string }> =>
      unwrap(await request(`/curated/${encodeURIComponent(id)}`, { method: 'DELETE' })),
  },

  entries: {
    /** Read-only. Server-side pagination — drive the grid off `pagination.total`. */
    list: async (query?: ListQuery): Promise<Paged<WisdomEntryRow>> =>
      paged<WisdomEntryRow>(await request(`/entries${qs(query as never)}`)),

    get: async (id: string): Promise<WisdomEntryDetail> =>
      unwrap<WisdomEntryDetail>(await request(`/entries/${encodeURIComponent(id)}`)),

    /**
     * GET /entries/export → raw CSV text. `include_text` is guarded server-side
     * and logged; only pass it when the admin explicitly opted in.
     */
    exportCsv: async (query?: {
      include_text?: boolean;
      category_id?: string;
      status?: string;
      limit?: number;
    }): Promise<string> => requestText(`/entries/export${qs(query as never)}`),
  },

  analytics: {
    summary: async (days?: number): Promise<AnalyticsSummary> =>
      unwrap<AnalyticsSummary>(await request(`/analytics/summary${qs({ days })}`)),

    entriesOverTime: async (days?: number): Promise<EntriesOverTimePoint[]> =>
      unwrap<EntriesOverTimePoint[]>(await request(`/analytics/entries-over-time${qs({ days })}`)) ||
      [],

    categoryDistribution: async (days?: number): Promise<CategoryDistributionRow[]> =>
      unwrap<CategoryDistributionRow[]>(
        await request(`/analytics/category-distribution${qs({ days })}`)
      ) || [],

    shareMethods: async (days?: number): Promise<ShareMethodRow[]> =>
      unwrap<ShareMethodRow[]>(await request(`/analytics/share-methods${qs({ days })}`)) || [],

    topPrompts: async (days?: number, limit?: number): Promise<TopPromptRow[]> =>
      unwrap<TopPromptRow[]>(await request(`/analytics/top-prompts${qs({ days, limit })}`)) || [],

    funnel: async (days?: number): Promise<FunnelResult> =>
      unwrap<FunnelResult>(await request(`/analytics/funnel${qs({ days })}`)),
  },
};

export default lifeAdviceApi;
